import { Injectable, signal, computed, effect } from '@angular/core'

export const THEMES = ['light', 'dark'] as const
export type Theme = (typeof THEMES)[number] // 'light' | 'dark'

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private currentTheme = signal<Theme>('dark')
  public readonly theme = computed(() => this.currentTheme())

  constructor() {
    const savedTheme = localStorage.getItem('theme') as Theme | null
    if (savedTheme) {
      this.currentTheme.set(savedTheme)
    }

    // Reactively update DOM and storage when theme changes
    effect(() => {
      const theme = this.currentTheme()
      localStorage.setItem('theme', theme)
      document.documentElement.setAttribute('data-theme', theme)

      const body = document.body
      if (theme === 'dark') {
        body.classList.add('dark-mode')
      } else {
        body.classList.remove('dark-mode')
      }
    })
  }

  public setTheme(theme: Theme): void {
    this.currentTheme.set(theme)
  }

  public toggleTheme(): void {
    const newTheme = this.currentTheme() === 'light' ? 'dark' : 'light'
    this.setTheme(newTheme)
  }

  public getTheme(): Theme {
    return this.currentTheme()
  }

  public initializeTheme(): void {
    const savedTheme = localStorage.getItem('theme') as Theme | null
    if (savedTheme) {
      this.setTheme(savedTheme)
    } else {
      this.setTheme('dark') // fallback
    }
  }

  public isValidTheme(value: any): value is Theme {
    return THEMES.includes(value)
  }
}
